import React, {useCallback, useEffect, useMemo, useState} from 'react'
import {
  ColumnDef,
  flexRender,
  getCoreRowModel,
  getSortedRowModel,
  SortingState,
  useReactTable,
} from '@tanstack/react-table'
import {pdf} from '@react-pdf/renderer'
import moment from 'moment'
import {KTCard, KTCardBody, KTIcon} from '../../../_websoft/helpers'
import {getECommerceLead, getECommerceLeads} from '../../api/e-commerce'
import notifyToast from '../../components/Toast/Toast'
import LoadingComponent from '../../components/LoadingComponent/LoadingComponent'
import TablePagination from '../../components/TablePagination/TablePagination'
import {PaginationModel} from '../../models/PaginationModel'
import {ECommerceLead, ECommerceLeadsParams, ECommerceOfferStatus} from '../../models/e-commerce'
import {ToastContainer} from 'react-toastify'
import LeadDetailsModal from './LeadDetailsModal'
import PotencijalniKlijentiPdf from './PotencijalniKlijentiPdf'
import 'react-toastify/dist/ReactToastify.css'

const countries = [
  'Albania',
  'Austria',
  'Bosnia and Herzegovina',
  'Croatia',
  'Hungary',
  'Kosovo',
  'Montenegro',
  'North Macedonia',
  'Serbia',
  'Slovenia',
]

const leadTypeBadgeClasses: Record<string, string> = {
  END_CUSTOMER: 'badge-light-primary',
  CONTRACTOR: 'badge-light-success',
  INSTALLER: 'badge-light-info',
  DISTRIBUTOR: 'badge-light-warning',
  DEVELOPER_OR_INVESTOR: 'badge-light-danger',
  OTHER: 'badge-light-dark',
}

const offerStatusBadgeStyles: Record<string, {label: string; backgroundColor: string; color: string}> = {
  NEW: {label: 'Novi lead', backgroundColor: '#e8fff3', color: '#0f9d58'},
  CONTACTED: {label: 'Kontaktiran', backgroundColor: '#eef6ff', color: '#3699ff'},
  WAITING: {label: 'Čekamo odgovor', backgroundColor: '#fff8dd', color: '#b58105'},
  QUALIFIED: {label: 'Kvalifikovan', backgroundColor: '#e1f0ff', color: '#246bfd'},
  PREPARING_OFFER: {label: 'Priprema ponude', backgroundColor: '#f8f5ff', color: '#7239ea'},
  OFFER_SENT: {label: 'Ponuda poslana', backgroundColor: '#fff4de', color: '#d98200'},
  NEGOTIATION: {label: 'Pregovori', backgroundColor: '#fff0f3', color: '#d63384'},
  WON: {label: 'Dobijen posao', backgroundColor: '#dfffe8', color: '#087f5b'},
  LOST: {label: 'Izgubljen posao', backgroundColor: '#fff1f1', color: '#d92121'},
  NOT_RELEVANT: {label: 'Nije relevantno', backgroundColor: '#f1f1f2', color: '#7e8299'},
}

const formatDate = (value?: string | null) => {
  if (!value || !moment(value).isValid()) {
    return ''
  }

  return moment(value).format('DD-MM-YYYY')
}

const getWebsiteUrl = (website: string) => {
  if (/^https?:\/\//i.test(website)) {
    return website
  }

  return `https://${website}`
}

const renderScore = (value?: number | string | null) => {
  if (value === undefined || value === null || value === '') {
    return ''
  }

  const numericValue = Number(value)

  if (!Number.isFinite(numericValue)) {
    return ''
  }

  const score = Math.round(Math.max(0, Math.min(100, numericValue)))
  const progressClass = score >= 80 ? 'bg-success' : score >= 60 ? 'bg-info' : score >= 40 ? 'bg-warning' : 'bg-danger'

  return (
    <div className='d-flex flex-column align-items-start gap-1' style={{minWidth: '85px'}} title={`${score}/100`}>
      <div className='progress w-100' style={{height: '7px'}}>
        <div className={`progress-bar ${progressClass}`} role='progressbar' style={{width: `${score}%`}} />
      </div>
      <span className='fw-bold text-nowrap'>{score}/100</span>
    </div>
  )
}

type Props = {
  fixedCountry?: string
}

const PotencijalniKlijenti: React.FC<Props> = ({fixedCountry}) => {
  const [data, setData] = useState<ECommerceLead[]>([])
  const [loading, setLoading] = useState(true)
  const [pdfLoading, setPdfLoading] = useState<number | null>(null)
  const [detailsLead, setDetailsLead] = useState<ECommerceLead | null>(null)
  const [country, setCountry] = useState(fixedCountry ?? '')
  const [sorting, setSorting] = useState<SortingState>([])
  const [pagination, setPagination] = useState<PaginationModel>({
    curPage: 1,
    totalPage: 1,
    perPage: 10,
  })
  const [params, setParams] = useState<ECommerceLeadsParams>({
    page: 1,
    per_page: 10,
    country: fixedCountry,
  })

  const updatePage = (page: number, perPage: number) => {
    setLoading(true)

    const requestParams = {
      ...params,
      page,
      per_page: perPage,
      ...(fixedCountry ? {country: fixedCountry} : {}),
    }

    getECommerceLeads(requestParams)
      .then((response) => {
        const responseData = response.data as any
        const paginationData =
          responseData.data && !Array.isArray(responseData.data) ? responseData.data : responseData
        const meta = paginationData.meta ?? responseData.meta ?? {}
        const responsePerPage =
          Number(paginationData.per_page ?? paginationData.perPage ?? meta.per_page ?? perPage) || perPage
        const responseTotal = Number(paginationData.total ?? meta.total ?? responseData.total ?? 0)
        const responseLastPage = Number(
          paginationData.last_page ?? paginationData.lastPage ?? meta.last_page ?? responseData.total_pages ?? 0
        )
        const totalPages = Math.max(1, responseLastPage || Math.ceil(responseTotal / responsePerPage) || 1)
        const leads = Array.isArray(paginationData.data)
          ? paginationData.data
          : Array.isArray(responseData.data)
            ? responseData.data
            : []

        setData(leads)
        setPagination({
          curPage: Number(paginationData.current_page ?? paginationData.currentPage ?? meta.current_page ?? page) || page,
          totalPage: totalPages,
          perPage: responsePerPage,
        })
      })
      .catch((error) => {
        console.error('Error loading e-commerce leads:', error)
        notifyToast(false, error)
        setData([])
      })
      .finally(() => setLoading(false))
  }

  useEffect(() => {
    updatePage(params.page, params.per_page)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params])

  const handleCountryChange = (value: string) => {
    if (fixedCountry) return

    setCountry(value)
    setParams((currentParams) => ({
      ...currentParams,
      page: 1,
      country: value || undefined,
    }))
  }

  const handleDetailsStatusUpdated = useCallback(
    (status: ECommerceOfferStatus | null) => {
      setData((currentData) =>
        currentData.map((lead) => (lead.id === detailsLead?.id ? {...lead, offer_status: status} : lead))
      )
    },
    [detailsLead?.id]
  )

  const openPdf = useCallback(async (lead: ECommerceLead) => {
    if (!lead.id) {
      notifyToast(false, 'Potencijalni klijent nema ID.')
      return
    }

    try {
      setPdfLoading(lead.id)
      const response = await getECommerceLead(lead.id)
      const responseData = response.data as any
      const leadData = responseData?.data ?? responseData
      const blob = await pdf(<PotencijalniKlijentiPdf lead={leadData} />).toBlob()
      const url = URL.createObjectURL(blob)

      window.open(url, '_blank')
      window.setTimeout(() => URL.revokeObjectURL(url), 60000)
    } catch (error: any) {
      console.error('Error generating e-commerce lead PDF:', error)
      notifyToast(false, error?.response?.data?.message ?? 'Greška prilikom generisanja PDF-a.')
    } finally {
      setPdfLoading(null)
    }
  }, [])

  const columns = useMemo<ColumnDef<ECommerceLead>[]>(
    () => [
      {
        header: 'ID',
        accessorKey: 'id',
      },
      {
        header: 'Naziv kompanije',
        accessorKey: 'company_name',
        cell: ({row}) => row.original.company_name || row.original.official_company_name || '',
      },
      {
        header: 'Web stranica',
        accessorKey: 'website',
        cell: ({row}) => {
          const website = row.original.website

          return website ? (
            <a href={getWebsiteUrl(website)} target='_blank' rel='noreferrer'>
              {website}
            </a>
          ) : (
            ''
          )
        },
      },
      {
        header: 'Država',
        accessorKey: 'country',
      },
      {
        header: 'Grad',
        accessorKey: 'city',
      },
      {
        header: 'Industrija',
        accessorKey: 'industry',
      },
      {
        header: 'Signal kupovine',
        accessorKey: 'buying_signal',
      },
      {
        header: 'Datum signala',
        accessorKey: 'signal_date',
        cell: ({row}) => formatDate(row.original.signal_date),
      },
      {
        header: 'Naziv projekta',
        accessorKey: 'project_name',
      },
      {
        header: 'Tip  klijenta',
        accessorKey: 'lead_type',
        cell: ({row}) => {
          const leadType = row.original.lead_type

          return leadType ? (
            <span className={`badge ${leadTypeBadgeClasses[leadType] ?? 'badge-light-secondary'}`}>
              {leadType}
            </span>
          ) : (
            ''
          )
        },
      },
      {
        header: 'Potencijal',
        accessorKey: 'lead_score',
        cell: ({row}) => renderScore(row.original.lead_score),
      },
      {
        header: 'Pouzdanost',
        accessorKey: 'confidence_score',
        cell: ({row}) => renderScore(row.original.confidence_score),
      },
      {
        header: 'Status ponude',
        accessorKey: 'offer_status',
        cell: ({row}) => {
          const status = row.original.offer_status
          const statusConfig = status ? offerStatusBadgeStyles[status] : undefined
          const badgeStyle = statusConfig
            ? {backgroundColor: statusConfig.backgroundColor, color: statusConfig.color}
            : {backgroundColor: '#f1f1f2', color: '#7e8299'}

          return status ? (
            <span className='badge fw-semibold' style={badgeStyle}>
              {statusConfig?.label ?? status}
            </span>
          ) : (
            ''
          )
        },
      },
      {
        id: 'details',
        header: 'Detalji',
        enableSorting: false,
        cell: ({row}) => (
          <button
            type='button'
            className='btn btn-icon btn-light-info btn-sm'
            onClick={() => setDetailsLead(row.original)}
            disabled={!row.original.id}
            title='Otvori detalje'
          >
            <KTIcon iconName='eye' className='fs-2' />
          </button>
        ),
      },
      {
        id: 'pdf',
        header: 'PDF',
        enableSorting: false,
        cell: ({row}) => (
          <button
            type='button'
            className='btn btn-icon btn-light-primary btn-sm'
            onClick={() => openPdf(row.original)}
            disabled={!row.original.id || pdfLoading === row.original.id}
            title='Otvori PDF'
          >
            <KTIcon iconName='document' className='fs-2' />
          </button>
        ),
      },
    ],
    [openPdf, pdfLoading]
  )

  const table = useReactTable({
    data,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    state: {sorting},
    onSortingChange: setSorting,
  })

  return (
    <KTCard>
      <ToastContainer />
      <LeadDetailsModal
        show={Boolean(detailsLead)}
        leadId={detailsLead?.id ?? null}
        leadSummary={detailsLead}
        onClose={() => setDetailsLead(null)}
        onStatusUpdated={handleDetailsStatusUpdated}
      />
      <KTCardBody className='py-4'>
        <div className='row mb-7'>
          <div className='col-md-4'>
            <label htmlFor='e-commerce-country' className='form-label fw-bold'>
              Država
            </label>
            <select
              id='e-commerce-country'
              className='form-select'
              value={fixedCountry ?? country}
              disabled={Boolean(fixedCountry)}
              onChange={(event) => handleCountryChange(event.target.value)}
            >
              <option value=''>Sve države</option>
              {countries.map((countryOption) => (
                <option key={countryOption} value={countryOption}>
                  {countryOption}
                </option>
              ))}
            </select>
          </div>
        </div>

        {loading ? (
          <LoadingComponent />
        ) : (
          <div className='table-responsive'>
            <table className='table align-middle table-bordered gy-5 no-footer'>
              <thead>
                {table.getHeaderGroups().map((headerGroup) => (
                  <tr key={headerGroup.id} className='text-start fw-bolder fs-7 text-uppercase gs-0 bg-dark p-2'>
                    {headerGroup.headers.map((header) => (
                      <th
                        className='text-white'
                        key={header.id}
                        onClick={header.column.getToggleSortingHandler()}
                        style={{cursor: header.column.getCanSort() ? 'pointer' : 'default'}}
                      >
                        {flexRender(header.column.columnDef.header, header.getContext())}
                        {header.column.getIsSorted() === 'asc' ? ' ↑' : ''}
                        {header.column.getIsSorted() === 'desc' ? ' ↓' : ''}
                      </th>
                    ))}
                  </tr>
                ))}
              </thead>
              <tbody className='text-gray-600'>
                {table.getRowModel().rows.length > 0 ? (
                  table.getRowModel().rows.map((row) => (
                    <tr key={row.id}>
                      {row.getVisibleCells().map((cell) => (
                        <td key={cell.id}>{flexRender(cell.column.columnDef.cell, cell.getContext())}</td>
                      ))}
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={columns.length}>
                      <div className='d-flex text-center w-100 align-content-center justify-content-center'>
                        No matching records found
                      </div>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}

        <TablePagination updatePage={updatePage} pagination={pagination} isLoading={loading} />
      </KTCardBody>
    </KTCard>
  )
}

export default PotencijalniKlijenti
